import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AuthShell } from "./ManagerLogin";

export default function CreateRestaurant() {
  const [form, setForm] = useState({ restaurant_name: "", manager_name: "", email: "", password: "" });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const { saveSession } = useAuth();
  const navigate = useNavigate();

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register-restaurant", form);
      saveSession(data);
      setCreated(data);
    } catch (err) {
      toast.error(formatApiError(err, "Could not create business"));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <AuthShell title="Business Created!" subtitle="Share this code with your staff so they can join">
        <div className="text-center" data-testid="business-created-panel">
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Your business code</div>
          <div className="font-mono font-bold text-4xl text-terra-600 tracking-[0.3em]" data-testid="created-join-code">
            {created.restaurant.join_code}
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(created.restaurant.join_code); toast.success("Code copied"); }}
            data-testid="created-copy-code-button"
            className="mt-4 h-11 px-5 rounded-xl border border-terra-400/50 text-terra-600 font-medium hover:bg-[#FDF0EB] transition-colors"
          >
            Copy Code
          </button>
          <button
            onClick={() => navigate("/manager")}
            data-testid="created-continue-button"
            className="mt-3 w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold text-lg hover:bg-terra-600 transition-colors"
          >
            Continue to Dashboard
          </button>
          <p className="text-xs text-slate-500 mt-4">You can always find this code at the top of your dashboard and on the Team page.</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Create Your Business" subtitle="You'll get a join code to share with your staff">
      <form onSubmit={submit} className="space-y-4" data-testid="create-restaurant-form">
        <Field testid="restaurant-name-input" placeholder="Business name" value={form.restaurant_name} onChange={set("restaurant_name")} />
        <Field testid="manager-name-input" placeholder="Your name" value={form.manager_name} onChange={set("manager_name")} />
        <Field testid="manager-email-input" type="email" placeholder="Email" value={form.email} onChange={set("email")} />
        <Field testid="manager-password-input" type="password" placeholder="Password (min 6 chars)" value={form.password} onChange={set("password")} />
        <button
          type="submit"
          disabled={busy}
          data-testid="create-restaurant-submit-button"
          className="w-full h-12 rounded-xl bg-terra-500 text-white font-display font-bold text-lg hover:bg-terra-600 disabled:opacity-50 transition-colors"
        >
          {busy ? "Creating…" : "Create Business"}
        </button>
      </form>
    </AuthShell>
  );
}

function Field({ testid, type = "text", ...props }) {
  return (
    <input
      type={type}
      required
      data-testid={testid}
      className="w-full h-12 px-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
      {...props}
    />
  );
}
